import { useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Sparkle, CircleNotch, ArrowRight } from "@phosphor-icons/react";

/** Tri IA : repère les photos qui semblent prises au sous-sol (= 876) ; Ralph confirme la sélection avant déplacement. */
export function SousSolTri({ photos = [], onChanged }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [sel, setSel] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const analysees = photos.filter((p) => p.ia).length;
  const suggestions = useMemo(() => photos.filter((p) => p.ia?.sous_sol && p.logement !== "876").sort((a, b) => (b.ia?.confiance || 0) - (a.ia?.confiance || 0)), [photos]);
  const checked = sel ?? new Set(suggestions.filter((p) => (p.ia?.confiance || 0) >= 0.5).map((p) => p.id));

  const analyser = async () => {
    setBusy(true);
    try {
      let r = await api.post("/brebeuf/photos/analyser-sous-sol");
      while (r.data.restant > 0) {
        setProgress({ fait: r.data.total - r.data.restant, total: r.data.total });
        onChanged(r.data.photos);
        await new Promise((res) => setTimeout(res, 3000));
        r = await api.get("/brebeuf/photos/analyse-statut");
        if (!r.data.en_cours && r.data.restant > 0) r = await api.post("/brebeuf/photos/analyser-sous-sol");
      }
      onChanged(r.data.photos);
      setSel(null);
      toast.success(`${r.data.total} photo(s) analysée(s).`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Analyse impossible"); } finally { setBusy(false); setProgress(null); }
  };
  const toggle = (id) => { const n = new Set(checked); n.has(id) ? n.delete(id) : n.add(id); setSel(n); };
  const deplacer = async () => {
    const ids = [...checked];
    if (!ids.length) return;
    if (!window.confirm(`Déplacer ${ids.length} photo(s) au 876 (sous-sol) ?`)) return;
    setBusy(true);
    try { const r = await api.post("/brebeuf/photos/deplacer", { ids, logement: "876" }); onChanged(r.data.photos); setSel(null); toast.success(`${r.data.deplacees} photo(s) déplacée(s) au 876.`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Déplacement impossible"); } finally { setBusy(false); }
  };
  const visibles = showAll ? suggestions : suggestions.slice(0, 60);

  return (
    <div className="rounded-xl border border-sky-500/30 bg-sky-500/5 p-4 space-y-3" data-testid="sous-sol-tri">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-bold text-sky-100">Tri IA — photos du sous-sol vers le 876</div>
          <div className="text-xs text-zinc-400">{analysees}/{photos.length} photo(s) analysée(s) · {suggestions.length} suggestion(s) encore rattachée(s) au 878. Rien n'est déplacé sans ta confirmation.</div>
        </div>
        <button type="button" disabled={busy} onClick={analyser} data-testid="sous-sol-analyser" className="inline-flex items-center gap-2 rounded-full bg-sky-500 hover:bg-sky-400 text-zinc-950 font-bold px-4 py-2 text-sm disabled:opacity-50">
          {busy && progress ? <><CircleNotch className="animate-spin" /> {progress.fait}/{progress.total}</> : <><Sparkle weight="fill" /> {analysees ? "Analyser les photos restantes" : "Analyser les 98 photos (IA)"}</>}
        </button>
      </div>
      {suggestions.length > 0 && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="sous-sol-suggestions">
            {visibles.map((p) => (
              <label key={p.id} className={`relative rounded-lg overflow-hidden border cursor-pointer ${checked.has(p.id) ? "border-sky-400" : "border-zinc-800 opacity-70"}`} data-testid={`sous-sol-card-${p.id}`}>
                <input type="checkbox" checked={checked.has(p.id)} onChange={() => toggle(p.id)} className="absolute top-2 left-2 w-4 h-4 accent-sky-400" data-testid={`sous-sol-check-${p.id}`} />
                <img src={p.url} alt={p.ia?.piece || ""} className="w-full h-28 object-cover" loading="lazy" />
                <div className="p-2 text-[11px] text-zinc-300"><b className="text-sky-200">{p.ia?.piece}</b> · {Math.round((p.ia?.confiance || 0) * 100)} %<div className="text-zinc-500 line-clamp-2">{p.ia?.indices}</div></div>
              </label>
            ))}
          </div>
          {suggestions.length > 60 && <button type="button" onClick={() => setShowAll((s) => !s)} className="text-xs text-sky-300">{showAll ? "Réduire" : `Voir les ${suggestions.length - 60} autres`}</button>}
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" disabled={busy || checked.size === 0} onClick={deplacer} data-testid="sous-sol-deplacer" className="inline-flex items-center gap-2 rounded-full bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold px-4 py-2 text-sm disabled:opacity-50"><ArrowRight weight="bold" /> Déplacer la sélection au 876 ({checked.size})</button>
            <button type="button" onClick={() => setSel(new Set(suggestions.map((p) => p.id)))} className="text-xs text-zinc-400 hover:text-zinc-100" data-testid="sous-sol-tout">Tout cocher</button>
            <button type="button" onClick={() => setSel(new Set())} className="text-xs text-zinc-400 hover:text-zinc-100" data-testid="sous-sol-rien">Tout décocher</button>
          </div>
        </>
      )}
      {analysees > 0 && suggestions.length === 0 && <p className="text-sm text-zinc-400" data-testid="sous-sol-vide">Aucune photo analysée comme « sous-sol » n'est encore rattachée au 878.</p>}
    </div>
  );
}
